import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { api } from "@/lib/api";
import { entryHours } from "@/lib/util";
import { usePhases } from "@/lib/phases";
import { Send, X } from "lucide-react";

export default function SubmitConfirmDialog({ totals, entries, projects = [], range, onCancel, onConfirm }) {
    const { phaseName } = usePhases();
    const projectsById = Object.fromEntries(projects.map((p) => [p.project_id, p]));

    const grouped = {};
    entries.forEach((e) => { (grouped[e.date] = grouped[e.date] || []).push(e); });
    const days = Object.keys(grouped).sort();

    // The certification text lives on the server and is versioned. We fetch it
    // rather than hardcoding a copy here, and send the version back on submit,
    // so a stale tab can never sign wording the employee didn't actually read.
    const [attestation, setAttestation] = useState(null);
    const [loadErr, setLoadErr] = useState(null);
    const [certified, setCertified] = useState(false);
    const [sending, setSending] = useState(false);
    const [err, setErr] = useState(null);

    useEffect(() => {
        let cancelled = false;
        api.get("/timecards/attestation")
            .then((r) => { if (!cancelled) setAttestation(r.data); })
            .catch(() => { if (!cancelled) setLoadErr("Couldn’t load the certification. Check your signal and try again."); });
        return () => { cancelled = true; };
    }, []);

    const canSend = !!attestation && certified && !sending;

    const send = async () => {
        setSending(true);
        setErr(null);
        try {
            await onConfirm({
                attested: true,
                attestation_version: attestation.version,
            });
        } catch (e) {
            setErr(e?.response?.data?.detail || "Couldn’t send your timecard. Try again.");
            setSending(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-brand-charcoal/70 backdrop-blur-sm flex items-end sm:items-center justify-center animate-fade-in" data-testid="submit-confirm-dialog">
            <div className="bg-brand-bone w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl shadow-lift max-h-[92vh] overflow-y-auto">
                <div className="bg-brand-navy text-brand-bone px-5 py-4 sm:rounded-t-2xl flex items-center justify-between sticky top-0 z-10">
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-brand-gold">Send to your foreman</div>
                        <div className="font-bold text-xl">{range}</div>
                    </div>
                    <button onClick={onCancel} className="p-2 hover:bg-brand-navy-dark rounded-lg" data-testid="submit-cancel"><X className="h-5 w-5" /></button>
                </div>
                <div className="p-5 space-y-4">
                    {/* The number being certified, and nothing beside it to
                        dilute it. "Hours Worked" and "Time Off" used to sit
                        here; time off is no longer recorded in this app, and
                        with it gone the two boxes were a subtotal and a zero
                        adding up to the figure underneath them. */}
                    <div className="bg-white border border-brand-charcoal/15 rounded-xl p-4 flex justify-between items-end">
                        <span className="text-xs uppercase tracking-widest text-brand-charcoal/60">Total hours</span>
                        <span className="font-bold text-3xl text-brand-navy tabular-nums" data-testid="confirm-total">
                            {(totals?.total || 0).toFixed(2)}
                        </span>
                    </div>

                    <div className="bg-white border border-brand-charcoal/15 rounded-xl p-4 space-y-3">
                        <div className="text-[10px] uppercase tracking-widest text-brand-navy">Days included</div>
                        {days.length === 0 ? (
                            <div className="text-sm text-brand-charcoal/60">No entries.</div>
                        ) : days.map((d) => (
                            <div key={d} className="text-sm">
                                <div className="font-bold">{dayjs(d).format("ddd, MMM D")}</div>
                                {/* What is being certified is the job and the
                                    phases, not a shift window -- so that is
                                    what gets shown back before signing. */}
                                <ul className="ml-4 mt-1 list-disc text-brand-charcoal/80">
                                    {grouped[d].map((e) => (
                                        <li key={e.entry_id} data-testid={`confirm-entry-${e.entry_id}`}>
                                            <span className="font-semibold text-brand-charcoal">
                                                {projectsById[e.project_id]?.name || "—"}
                                            </span>
                                            <span className="ml-2 tabular-nums">{entryHours(e).toFixed(2)}h</span>
                                            {(e.phase_numbers || []).length > 0 && (
                                                <span className="block text-xs text-brand-charcoal/70">
                                                    {e.phase_numbers.map((n) => phaseName(n)).join(" · ")}
                                                </span>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>

                    {/* ---- certification ---- */}
                    {loadErr ? (
                        <div className="bg-white border border-brand-danger/30 rounded-xl p-4 text-sm text-brand-danger" data-testid="attestation-load-error">
                            {loadErr}
                        </div>
                    ) : !attestation ? (
                        <div className="bg-white border border-brand-charcoal/15 rounded-xl p-4 text-sm text-brand-charcoal/60" data-testid="attestation-loading">
                            Loading…
                        </div>
                    ) : (
                        <div className="bg-white border-2 border-brand-gold rounded-xl p-4 space-y-4" data-testid="attestation-block">
                            <div className="text-[10px] uppercase tracking-widest text-brand-navy">Before you send</div>

                            <label className="flex gap-3 items-start cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={certified}
                                    onChange={(e) => setCertified(e.target.checked)}
                                    className="mt-1 h-6 w-6 shrink-0 accent-brand-navy"
                                    data-testid="attestation-checkbox"
                                />
                                <span className="text-sm text-brand-charcoal leading-relaxed">{attestation.text}</span>
                            </label>
                        </div>
                    )}

                    {err && <div className="text-sm text-brand-danger" data-testid="submit-error">{err}</div>}

                    <p className="text-xs text-brand-charcoal/60 text-center">
                        You can still add or fix hours after sending — until your foreman approves.
                    </p>
                    <div className="flex gap-2">
                        <button onClick={onCancel} className="flex-1 h-12 rounded-xl bg-brand-stone hover:bg-brand-gold/40 text-brand-charcoal font-bold" data-testid="submit-cancel-button">Not yet</button>
                        <button
                            onClick={send}
                            disabled={!canSend}
                            className="flex-1 h-12 rounded-xl bg-brand-navy hover:bg-brand-navy-dark text-brand-bone font-bold flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid="submit-confirm-button"
                        >
                            <Send className="h-4 w-4" /> {sending ? "Sending…" : "Send it"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
